import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getPaymentStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const res = await getPaymentStatus(sessionId);
        setAmount(res.amount);
        if (res.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (res.payment_status === "expired" || res.status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts > 8) {
        setStatus("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 pt-32">
      <div className="max-w-lg text-center" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-sage animate-spin mx-auto mb-6" />
            <h1 className="font-serif text-4xl text-forest">Confirming your order…</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-sage mx-auto mb-6" />
            <h1 className="font-serif text-5xl text-forest mb-4">Thank you.</h1>
            <p className="text-forest-soft text-lg mb-2">Your order is confirmed{amount ? ` — $${amount}` : ""}.</p>
            <p className="text-forest-soft mb-8">We&rsquo;re hand-packing your blends with love in Racine.</p>
            <Link to="/shop" data-testid="success-continue" className="inline-block rounded-full bg-forest hover:bg-terracotta text-cream px-8 py-3.5 text-sm uppercase tracking-widest font-semibold" style={{ transition: "background-color 0.3s ease" }}>
              Continue shopping
            </Link>
          </>
        )}
        {(status === "pending" || status === "checking") === false && status !== "paid" && (
          <>
            <h1 className="font-serif text-4xl text-forest mb-4">
              {status === "failed" ? "Payment not completed" : "Still processing"}
            </h1>
            <p className="text-forest-soft mb-8">
              {status === "failed"
                ? "Your payment didn't go through. Please try again."
                : "Your payment is taking a moment. We'll email you once it's confirmed."}
            </p>
            <Link to="/shop" className="inline-block rounded-full bg-forest text-cream px-8 py-3.5 text-sm uppercase tracking-widest font-semibold">Back to shop</Link>
          </>
        )}
      </div>
    </div>
  );
}
